import * as React from "react";
import {CSSProperties} from "react";
import "./searchDropDown.scss";
import IconLarge from "../../atoms/Icon/IconLarge";
import {Icon} from "../../atoms/Icon/Icon";
import InputWithIcon from "../../atoms/Input/InputWithIcon";
import {option, Option} from "ts-option";
import {isDefined} from "../../../../utils/Utils";
import Loading from "../../../../components/ui/loading/Loading";
import Checkbox from "../../atoms/Checkbox/Checkbox";
import classNames = require("classnames");

interface IProps<T> {
	/**
	 * List of items.
	 */
	items: T[];
	/**
	 * Converts item to item label.
	 * @param {T} item
	 * @return {string}
	 */
	itemToLabel: (item: T) => string;
	/**
	 *
	 * @param {T} item
	 * @returns {string | JSX.Element}
	 */
	itemRowRenderer?: (item: T, isSelected: boolean) => string | JSX.Element;
	/**
	 * Converts item to item id.
	 * @param {T} item
	 * @return {string}
	 */
	itemToId: (item: T) => string;
	/**
	 * Indicates whether this item has special style.
	 * @param {T} item
	 * @return {boolean}
	 */
	isItemSpecial?: (item: T) => boolean;
	/**
	 * On input change callback.
	 */
	onChange?: (item: T | T[]) => void;
	/**
	 * Selected item/s id/ids.
	 */
	selectedItemId?: string | string[];
	/**
	 * Search input icon.
	 */
	searchInputIcon?: string;
	/**
	 * Search input icon.
	 */
	prependSearchInputIcon?: string;
	/**
	 * Search input placeholder.
	 */
	placeholder?: string;
	/**
	 * If multiselect should by able
	 */
	multi?: boolean;
	/**
	 * Custom styles for option component.
	 */
	optionInlineStyle?: CSSProperties;
	/**
	 * Optional component displayed between the input and the list
	 */
	componentBetweenInputAndList?: JSX.Element;
	/**
	 * If drop down data are loading
	 */
	isLoading?: boolean;
	/**
	 * Indicates if select all option is available
	 */
	selectAll?: boolean;
}

interface IState<T> {
	/**
	 * List of filtered items.
	 */
	filteredItems: T[];
	/**
	 * String to filter results on list.
	 */
	searchString: string;
	/**
	 * Item with focus
	 */
	itemWithFocus: Option<T>;
}

export default class SearchDropDown<T> extends React.PureComponent<IProps<T>, IState<T>> {

	constructor(props: IProps<T>) {
		super(props);
		this.state = {
			filteredItems: this.props.items,
			searchString: "",
			itemWithFocus: option(this.props.items[0])
		};
		this.filterList = this.filterList.bind(this);
		this.onSelect = this.onSelect.bind(this);
		this.unSelect = this.unSelect.bind(this);

	}

	componentWillReceiveProps(nextProps: IProps<T>) {
		if (!this.props.multi && nextProps.selectedItemId) {
			this.setState({
				searchString: nextProps.itemToLabel(
					nextProps.items.filter((item) => nextProps.itemToId(item) === nextProps.selectedItemId)[0])
			});
		}
	}

	render() {
		return (
			<div className={"m-search-dropdown"}>
				<div className={"m-search-dropdown_input"}>
					<InputWithIcon type={"text"}
								   value={this.state.searchString}
								   placeholder={this.props.placeholder}
								   appendIcon={this.props.searchInputIcon}
								   prependIcon={this.props.prependSearchInputIcon}
								   onChange={this.filterList}
								   focus={true}
								   onConfirm={() => {
									   this.state.itemWithFocus.map(iwf => this.props.multi && this.isItemSelected(iwf) ?
										   this.unSelect(iwf) : this.onSelect(iwf))

								   }}/>
				</div>
				{this.props.componentBetweenInputAndList}
				<ul>
					{this.props.isLoading ? <Loading isLoading={true} spinnerSize={32}/> :
						<>
							{this.props.multi && this.props.selectAll &&
							<li key={-1}>
								<Checkbox checked={this.areAllSelected()} label={"All"} onChange={this.onAllChange}
										  customClass={"m-search-dropdown_select-all"}/>
							</li>}
							{this.state.filteredItems.map((item, index) => {
								const isItemSelected = this.isItemSelected(item);
								return (
									<li key={index}
										onClick={(e) => {
											e.stopPropagation();
											this.props.multi && isItemSelected ? this.unSelect(item) : this.onSelect(item)
										}}
										onMouseOver={() => this.setState({itemWithFocus: option(item)})}
										className={classNames({
											"is-focused": this.state.itemWithFocus.map(
												iwf => this.props.itemToId(iwf) === this.props.itemToId(item)).getOrElse(false),
											"is-selected": isItemSelected,
											"is-italic": this.isItemSpecial(item)
										})}
										style={this.props.optionInlineStyle}>
										{this.props.itemRowRenderer
											? this.props.itemRowRenderer(item, isItemSelected)
											: <React.Fragment>
												{this.props.itemToLabel(item)}
												{isItemSelected && <IconLarge icon={Icon.CheckMark}/>}
											</React.Fragment>
										}
									</li>
								);
							})}</>}
				</ul>
			</div>
		);
	}

	filterList(evt: React.ChangeEvent<HTMLInputElement>) {
		const searchString = evt.target.value;
		const filteredItems = this.props.items.filter(
			(item: T) => this.props.itemToLabel(item).toLowerCase().indexOf(searchString.toLowerCase()) !== -1);
		this.setState({
			filteredItems,
			searchString,
			itemWithFocus: option(filteredItems[0])
		});
	}

	/**
	 * On select callback.
	 * @param {T} item
	 */
	onSelect(item: T) {
		if (isDefined(this.props.onChange)) {
			this.props.multi
				? this.props.onChange((this.props.selectedItemId as string[]).concat(this.props.itemToId(item))
					.map(id => this.props.items.filter(item => this.props.itemToId(item) === id)[0]))
				: this.props.onChange(item);
		}
	}

	/**
	 * Un select callback.
	 * @param {T} item
	 */
	unSelect(item: T) {
		if (this.props.multi && isDefined(this.props.onChange)) {
			const selectedItemIds = (this.props.selectedItemId as string[]).filter(i => i !== this.props.itemToId(item));
			this.props.onChange(selectedItemIds.map(id => this.props.items.filter(item => this.props.itemToId(item) === id)[0]));
		}
	}

	/**
	 * Indicates if item is selected.
	 * @param {T} item
	 * @return {boolean}
	 */
	isItemSelected(item: T): boolean {
		if (this.props.multi) {
			return !!(this.props.selectedItemId as string[]).filter(i => i === this.props.itemToId(item)).length
		}
		else {
			return this.props.itemToId(item) === this.props.selectedItemId
		}
	}

	isItemSpecial(item: T) {
		return this.props.isItemSpecial && this.props.isItemSpecial(item);
	}

	areAllSelected() {
		return this.props.items.length === (this.props.selectedItemId as string[]).length;
	}

	onAllChange = () => {
		let selectedItems: T[];
		if (this.areAllSelected()) {
			selectedItems = [];
		}
		else {
			selectedItems = this.props.items;
		}
		this.props.multi && this.props.onChange(selectedItems);
	}
}
